import type { QueryClient } from "@tanstack/react-query";
import { isSessionMarkerKnown } from "./auth-cookie";
import { queryKeys } from "./query-keys";
import { fetchUserProfile } from "./user-profile";

/**
 * Harte Obergrenze für die Probe. Sie sitzt im beforeLoad von _app und _setup
 * und blockiert dort das erste Rendern — ein hängendes Backend darf daraus
 * keine unbegrenzt weiße Seite machen.
 *
 * Großzügig bemessen, weil ein fälschlicher Abbruch teurer ist als Wartezeit:
 * Die Probe läuft nur im geschützten Bereich, wo der Kunde ohnehin auf Inhalte
 * wartet, und nur solange der Marker unbekannt ist. Ein zu knapper Wert würde
 * im schlechten Mobilfunknetz genau das Symptom erzeugen, gegen das die Probe
 * antritt: Login-Fenster trotz gültiger Session.
 */
const PROBE_TIMEOUT_MS = 5000;

let probe: Promise<void> | null = null;

/**
 * Klärt einmalig pro Page-Load, ob eine Backend-Session existiert, falls das
 * hasUser-Marker-Cookie fehlt.
 *
 * Ohne diese Probe kann sich ein verlorener Marker nie wieder heilen: die
 * Guards leiten sofort auf /login, und /login fragt seinerseits nichts ab. Der
 * Kunde bliebe trotz gültiger Session ausgesperrt, bis er sich von Hand neu
 * anmeldet.
 *
 * Aufgerufen wird sie nur dort, wo der Marker gleich über einen Redirect
 * entscheidet (_app, _setup). Die Landing-Page und /login proben bewusst nicht.
 *
 * Der Request läuft bewusst an react-query vorbei — der globale
 * QueryCache-onError würde bei anonymen Besuchern sonst das Login-Modal
 * öffnen. Der BFF setzt bei seiner Antwort hasUser=true bzw. hasUser=false,
 * damit ist der Zustand danach bekannt und die Probe entfällt.
 */
export function ensureSessionMarker(queryClient: QueryClient): Promise<void> {
	if (isSessionMarkerKnown()) {
		return Promise.resolve();
	}

	// Parallele Aufrufer desselben Durchlaufs teilen sich einen Request.
	// Memoisiert bleibt das Ergebnis aber nur, wenn es den Marker tatsächlich
	// geklärt hat — dann greift ohnehin schon isSessionMarkerKnown() oben.
	// Endete die Probe im Timeout oder in einem Netzwerkfehler, wird die
	// Memoisierung verworfen, damit eine spätere Navigation es erneut versucht.
	// Sonst sperrt ein einziger langsamer Roundtrip den Kunden für den ganzen
	// Page-Load aus, obwohl seine Session gültig ist.
	probe ??= runProbe(queryClient).finally(() => {
		if (!isSessionMarkerKnown()) {
			probe = null;
		}
	});

	return probe;
}

function runProbe(queryClient: QueryClient): Promise<void> {
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

	return fetchUserProfile(controller.signal)
		.then((user) => {
			// Ergebnis in den Cache legen, damit der Profil-Request der Root-Route
			// nicht direkt ein zweites Mal läuft. Die Form muss der von useUser
			// entsprechen: dort steht der entpackte User, nicht die Antwort.
			queryClient.setQueryData(queryKeys.user.profile(), user);
		})
		.catch(() => {
			// 401 ist der Normalfall für anonyme Besucher. Bricht der Timeout ab,
			// bleibt der Marker unbekannt — der Guard leitet dann wie bisher auf
			// /login. Das ist das akzeptierte Verhalten, kein Fehler.
		})
		.finally(() => {
			clearTimeout(timeout);
		});
}

/** Nur für Tests: den Modulzustand zwischen Fällen zurücksetzen. */
export function resetSessionProbe(): void {
	probe = null;
}
